import { evalAuthHeaders, getEvalBase } from '../../evalApiBase'
import {
  EvalCase,
  EvalCaseCreateInput,
  EvalCaseListResponse,
  EvalCaseTagsUpdateInput,
  EvalCaseUpdateInput,
} from './casesAdminModel'

async function readErrorDetail(res: Response): Promise<string | null> {
  try {
    const body = await res.json()
    if (body && typeof body.detail === 'string') {
      return body.detail
    }
  } catch {
    // fall through
  }
  return null
}

export async function listCases(): Promise<EvalCaseListResponse> {
  const res = await fetch(`${getEvalBase()}/v1/eval-cases`, {
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    throw new Error(`Cases list failed: ${res.status}`)
  }
  return res.json()
}

export async function createCase(input: EvalCaseCreateInput): Promise<EvalCase> {
  const res = await fetch(`${getEvalBase()}/v1/eval-cases`, {
    method: 'POST',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    // Surface the backend detail — name collisions (409) and body validation
    // failures (422, e.g. unknown scenario) carry actionable messages.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Case create failed: ${res.status}`)
  }
  return res.json()
}

export async function updateCase(input: EvalCaseUpdateInput): Promise<EvalCase> {
  const { case_id, ...payload } = input
  const res = await fetch(`${getEvalBase()}/v1/eval-cases/${case_id}`, {
    method: 'PUT',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    // Update is body-only; the name is immutable and saving appends a version.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Case update failed: ${res.status}`)
  }
  return res.json()
}

export async function setCaseTags(
  input: EvalCaseTagsUpdateInput,
): Promise<EvalCase> {
  // Version-free: replaces the case's tag join rows without appending a case
  // body version (#92). An unknown/soft-deleted tag id returns 422.
  const res = await fetch(`${getEvalBase()}/v1/eval-cases/${input.case_id}/tags`, {
    method: 'PUT',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag_ids: input.tag_ids }),
  })
  if (!res.ok) {
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Case tags update failed: ${res.status}`)
  }
  return res.json()
}

export async function deleteCase(caseId: string): Promise<void> {
  const res = await fetch(`${getEvalBase()}/v1/eval-cases/${caseId}`, {
    method: 'DELETE',
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Case delete failed: ${res.status}`)
  }
}
